/**
 * Game — engine core. 1:1 port of Game.java, with three things swapped for browser reality:
 *
 *   1. Window/BufferStrategy    -> a <canvas> sized to the viewport.
 *   2. The busy-wait frame cap  -> requestAnimationFrame (the browser already paces us to
 *                                  the display refresh; the Java spin existed only to dodge
 *                                  Windows' 15.6ms sleep quantization, which does not apply).
 *   3. AWT listeners            -> DOM events funnelled through the java/AwtEvent shims.
 *
 * The 60Hz fixed-step tick and every visual effect are unchanged.
 */
import { Color } from './java/Color';
import { Graphics2D, Font, BufferedImage, GradientPaint, BasicStroke, AlphaComposite, RenderingHints, Transparency } from './java/Graphics2D';
import { Random } from './java/Random';
import { MouseEvent, MouseWheelEvent, KeyEvent, domKeyToVK } from './java/AwtEvent';
import { unlock as unlockAudio } from './java/Slick';

import { STATE } from './STATE';
import { ID } from './ID';
import { GameObject } from './GameObject';
import { Handler } from './Handler';
import { HUD } from './HUD';
import { Spawn } from './Spawn';
import { Menu } from './Menu';
import { Shop } from './Shop';
import { MusicPlayer } from './MusicPlayer';
import { Player } from './Player';
import { BasicEnemy } from './BasicEnemy';
import { MenuParticle } from './MenuParticle';
import { EnemyBossBullet } from './EnemyBossBullet';
import { KeyInput } from './KeyInput';
import { TrailPool } from './TrailPool';
import { GamePalette } from './GamePalette';
import { Settings } from './Settings';
import { PageRenderer } from './PageRenderer';
import { AudioPlayer } from './AudioPlayer';
import { Profile } from './Profile';
import { Achievements } from './Achievements';
import { DailyChallenge } from './DailyChallenge';
import { TouchControls } from './TouchControls';
import { isTouchDevice } from './Device';

export class Game {
  // Logical resolution — HEIGHT is fixed, WIDTH adapts to screen aspect ratio
  static WIDTH = 1280;
  static readonly HEIGHT = 720;

  /**
   * The narrowest logical width the UI can actually survive. Below this we letterbox rather
   * than shrink — see calculateWindowSize().
   */
  static readonly MIN_WIDTH = 1280;

  // Scaling
  private static scale = 1.0;
  private static windowWidth = 1280;
  private static windowHeight = 720;
  /** Letterbox offsets in device pixels (non-zero only when the viewport is too narrow). */
  private static offsetX = 0;
  private static offsetY = 0;

  private running = false;

  // Stores which state we came from when pausing (Game or Shop)
  static pausedFrom: STATE = STATE.Game;

  /** Live game state. Named STATE.* — import { STATE } from './STATE'. */
  static gameState: STATE = STATE.Menu;
  /** Kept so `Game.STATE.Menu` still reads naturally from ported code. */
  static readonly STATE = STATE;

  diff = 1;

  private fps = 0;

  // Cached colors for render (avoid per-frame allocation)
  private static readonly DYING_OVERLAY = new Color(8, 10, 16, 180);
  private static readonly DMG_RED_CLEAR = new Color(235, 50, 50, 0);
  private static readonly SLOWMO_WHITE_CLEAR = new Color(255, 255, 255, 0);

  // Attempt display
  static currentAttempt = 0;
  static attemptFade = 0;
  private static readonly ATTEMPT_FONT = new Font('Arial', Font.BOLD, 20);
  private static readonly ATTEMPT_NUM_FONT = new Font('Arial', Font.BOLD, 28);
  private static readonly BOSS_WARN_FONT = new Font('Arial', Font.BOLD, 72);
  private static readonly BOSS_SUB_FONT = new Font('Arial', Font.BOLD, 22);
  private static readonly PAUSED_FONT = new Font('Arial', Font.BOLD, 125);

  // End-of-run stats
  lastScore = 0;
  lastIsHighScore = false;
  lastLevel = 0;
  lastTime = '';
  lastEnemies = 0;
  lastUpgrades = 0;
  lastHealthUps = 0;
  lastSpeedUps = 0;
  lastRefills = 0;
  lastDifficulty = '';
  lastXpEarned = 0;
  lastCoinsEarned = 0;
  lastLeveledUp = false;
  lastProfileLevel = 1;

  // Run-tracking for Profile (accumulate during run, submit on death)
  runBossesDefeated = 0;
  runDamageTaken = 0;
  runLongestStreak = 0;
  runEncBasic = 0;
  runEncFast = 0;
  runEncSmart = 0;
  runEncHard = 0;
  runEncBoss = 0;
  runEnemiesKilled = 0;
  lastEnemiesKilled = 0;

  static addBossDefeated(): void {
    if (Game.instance !== null) Game.instance.runBossesDefeated++;
  }

  static addDamage(dmg: number): void {
    if (Game.instance !== null) Game.instance.runDamageTaken += dmg;
  }

  static addEnemyKilled(): void {
    if (Game.instance !== null) Game.instance.runEnemiesKilled++;
    Game.triggerScreenShake(3);
  }

  static getRunKills(): number {
    return Game.instance !== null ? Game.instance.runEnemiesKilled : 0;
  }

  static addKillBonus(bonus: number): void {
    if (Game.hudRef !== null) Game.hudRef.addKillBonus(bonus);
  }

  resetRunTracking(): void {
    this.runBossesDefeated = 0;
    this.runDamageTaken = 0;
    this.runLongestStreak = 0;
    this.runEncBasic = 0;
    this.runEncFast = 0;
    this.runEncSmart = 0;
    this.runEncHard = 0;
    this.runEncBoss = 0;
    this.runEnemiesKilled = 0;
    this.slowmoAccum = 0;
  }

  private static instance: Game | null = null;

  private r: Random;
  private static handlerRef: Handler | null = null;
  private static hudRef: HUD | null = null;
  private static spawnerRef: Spawn | null = null;

  static dailyMode = false;
  static combatMode = false;
  static mouseGameX = 0;
  static mouseGameY = 0;
  static mouseShootHeld = false;

  static seedSpawner(seed: number | bigint): void {
    if (Game.spawnerRef !== null) Game.spawnerRef.setSeed(seed);
  }

  private handler: Handler;
  private hud: HUD;
  private spawner: Spawn;
  private menu: Menu;
  shop: Shop;
  private musicPlayer: MusicPlayer;
  private keyInput: KeyInput;

  /** On-canvas thumb controls. Only live on a coarse-pointer device. */
  private touch = new TouchControls();
  private isTouch = false;

  // Canvas
  private readonly canvas: HTMLCanvasElement;
  private readonly g: Graphics2D;

  // Screen shake + damage flash
  private static shakeIntensity = 0;
  private static flashAlpha = 0;
  private static shakeRng = new Random();

  // Level-up pulse
  private static levelUpFlash = 0;

  // Boss intro cinematic
  private static bossIntroTimer = 0;
  private static bossIntroActive = false;
  private static enemyExplosions: number[][] | null = null; // [x, y, vx, vy, size, r, g, b, life]

  static triggerBossIntro(): void {
    Game.bossIntroActive = true;
    Game.bossIntroTimer = 0;
    Game.shakeIntensity = 4;
  }

  static isBossIntroActive(): boolean {
    return Game.bossIntroActive;
  }

  static setEnemyExplosions(particles: number[][] | null): void {
    Game.enemyExplosions = particles;
  }

  // Bullet cascade — queued bullet positions sorted by distance from boss death point
  // [x, y, delay (ticks until explosion), exploded (0/1)]
  private static bulletCascade: number[][] | null = null;
  private static cascadeTick = 0;
  private static cascadeActive = false;
  // Cascade particles — separate from enemyExplosions so they can coexist
  private static cascadeParticles: number[][] = Array.from({ length: 200 }, () => new Array<number>(9).fill(0));
  private static cascadeParticleCount = 0;

  static triggerBulletCascade(bossX: number, bossY: number, handler: Handler): void {
    const bullets: number[][] = [];
    const toRemove: GameObject[] = [];

    const objs = handler.getObjects();
    for (let i = 0; i < objs.length; i++) {
      const obj = objs[i];
      if (obj instanceof EnemyBossBullet) {
        const bx = obj.getX() + 8;
        const by = obj.getY() + 8;
        const dist = Math.sqrt((bx - bossX) * (bx - bossX) + (by - bossY) * (by - bossY));
        // Delay proportional to distance — closer bullets explode first
        const delay = Math.trunc(dist / 6);
        bullets.push([bx, by, delay, 0]);
        toRemove.push(obj);
      }
    }

    // Remove bullets from handler — they'll be rendered as cascade particles instead
    for (const obj of toRemove) {
      handler.removeObject(obj);
    }

    if (bullets.length > 0) {
      Game.bulletCascade = bullets;
      Game.cascadeTick = 0;
      Game.cascadeActive = true;
      Game.cascadeParticleCount = 0;
    }
  }

  // Speed lines + camera intensity + look-ahead
  private playerSpeed = 0;
  private playerVelX = 0;
  private playerVelY = 0;
  private cameraZoom = 1;
  private cameraZoomTarget = 1;
  private camOffsetX = 0;
  private camOffsetY = 0;
  private static readonly CAM_LOOK_AHEAD = 15;
  private static readonly CAM_LERP = 0.04;
  private speedLines: number[][] = Array.from({ length: 20 }, () => new Array<number>(5).fill(0)); // [x, y, angle, length, life]
  private speedLineTimer = 0;

  // Geometric background layers
  private geoPhase = 0;
  private geoCache: BufferedImage | null = null;
  private geoCacheFrame = 0;

  // Screen transitions
  private lastState: STATE = STATE.Menu;
  private transitionAlpha = 0;
  private transitionZoom = 0; // extra zoom for menu→game
  private static readonly TRANS_SPEED = 0.12;

  // Neon walls — impact flares [x, y, intensity, side(0=top,1=bottom,2=left,3=right)]
  private static wallFlares: number[][] = Array.from({ length: 32 }, () => new Array<number>(4).fill(0));
  private static wallFlareCount = 0;
  private static wallPulsePhase = 0;

  // Bounce particles — [x, y, vx, vy, life, r, g, b]
  private static bounceParticles: number[][] = Array.from({ length: 80 }, () => new Array<number>(8).fill(0));
  private static bounceParticleCount = 0;
  private static bounceRng = new Random();

  static wallHit(hitX: number, hitY: number, side: number, cr = 235, cg = 87, cb = 87): void {
    if (Game.wallFlareCount < Game.wallFlares.length) {
      Game.wallFlares[Game.wallFlareCount] = [hitX, hitY, 1, side];
      Game.wallFlareCount++;
    }
    // Spawn 5-7 bounce particles
    const count = 5 + Game.bounceRng.nextInt(3);
    for (let i = 0; i < count && Game.bounceParticleCount < Game.bounceParticles.length; i++) {
      // Scatter away from the wall
      let angle: number;
      if (side === 0) angle = Math.PI * 0.25 + Game.bounceRng.nextFloat() * Math.PI * 0.5; // down
      else if (side === 1) angle = -Math.PI * 0.25 - Game.bounceRng.nextFloat() * Math.PI * 0.5; // up
      else if (side === 2) angle = -Math.PI * 0.25 + Game.bounceRng.nextFloat() * Math.PI * 0.5; // right
      else angle = Math.PI * 0.75 + Game.bounceRng.nextFloat() * Math.PI * 0.5; // left
      const speed = 1.5 + Game.bounceRng.nextFloat() * 3;
      Game.bounceParticles[Game.bounceParticleCount] = [
        hitX, hitY,
        Math.cos(angle) * speed, Math.sin(angle) * speed,
        0.7 + Game.bounceRng.nextFloat() * 0.3,
        cr, cg, cb,
      ];
      Game.bounceParticleCount++;
    }
  }

  // Death animation
  private deathTimer = 0;
  private deathFlash = 0;
  private deathParticles: number[][] | null = null; // [x, y, vx, vy, size, r, g, b]
  private deathPlayerX = 0;
  private deathPlayerY = 0;

  static triggerHit(): void {
    Game.shakeIntensity = 8;
    Game.flashAlpha = 0.6;
  }

  static triggerScreenShake(intensity: number): void {
    Game.shakeIntensity = Math.max(Game.shakeIntensity, intensity);
  }

  // Time scale for slow-motion ability
  private static timeScale = 1;
  private static timeScaleTarget = 1;

  static setTimeScale(scale: number): void {
    Game.timeScaleTarget = Math.max(0.1, Math.min(1, scale));
  }

  static getTimeScale(): number { return Game.timeScale; }

  static getHandler(): Handler | null { return Game.handlerRef; }

  static getHud(): HUD | null { return Game.hudRef; }

  private slowmoAccum = 0;

  static getLevelProgress(): number {
    return Game.spawnerRef !== null ? Game.spawnerRef.getLevelProgress() : 0;
  }

  static isBossActive(): boolean {
    return Game.spawnerRef !== null && Game.spawnerRef.isBossActive();
  }

  static getPlayerMultiplier(): number {
    if (Game.handlerRef === null) return 1;
    const objs = Game.handlerRef.getObjects();
    for (let i = 0; i < objs.length; i++) {
      const obj = objs[i];
      if (obj instanceof Player) return obj.getMultiplier();
    }
    return 1;
  }

  static triggerLevelUp(): void {
    Game.levelUpFlash = 1;
  }

  // -------------------------------------------------------------------------

  constructor(canvas: HTMLCanvasElement) {
    Game.instance = this;

    this.canvas = canvas;
    const ctx = canvas.getContext('2d', { alpha: false });
    if (!ctx) throw new Error('Canvas 2D context unavailable');
    this.g = new Graphics2D(ctx);

    // Calculate display size — largest 16:9 that fits the screen
    this.calculateWindowSize();

    this.handler = new Handler();
    Game.handlerRef = this.handler;
    this.hud = new HUD();
    Game.hudRef = this.hud;
    this.shop = new Shop(this.handler, this.hud);
    this.menu = new Menu(this, this.handler, this.hud);
    this.musicPlayer = new MusicPlayer();
    this.keyInput = new KeyInput(this.handler, this);

    this.isTouch = isTouchDevice();
    if (this.isTouch) canvas.classList.add('touch');
    TouchControls.debugEnabled = new URLSearchParams(location.search).has('touchdebug');
    TouchControls.debugIsTouch = this.isTouch;
    this.attachListeners();

    AudioPlayer.load();
    Settings.getMusicVolume(); // Force settings load & volume sync
    AudioPlayer.play();

    this.spawner = new Spawn(this.handler, this.hud, this);
    Game.spawnerRef = this.spawner;
    this.r = new Random();

    if (Game.gameState === STATE.Game) {
      this.hud.setLevel(1);
      this.hud.setScore(0);
      this.handler.addObject(new Player(Game.WIDTH / 2 - 32, Game.HEIGHT / 2 - 32, ID.Player, this.handler));
      this.handler.addObject(new BasicEnemy(this.r.nextInt(Game.WIDTH - 50), this.r.nextInt(Game.HEIGHT - 50), ID.BasicEnemy, this.handler));
    }
  }

  // ---- sizing ----

  private calculateWindowSize(): void {
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const cssW = Math.max(320, window.innerWidth);
    const cssH = Math.max(240, window.innerHeight);

    Game.windowWidth = Math.round(cssW * dpr);
    Game.windowHeight = Math.round(cssH * dpr);

    this.canvas.width = Game.windowWidth;
    this.canvas.height = Game.windowHeight;
    this.canvas.style.width = cssW + 'px';
    this.canvas.style.height = cssH + 'px';

    // Scale uniformly by height — WIDTH adapts to screen aspect ratio.
    Game.scale = Game.windowHeight / Game.HEIGHT;
    let logicalWidth = Math.trunc(Game.windowWidth / Game.scale);

    if (logicalWidth >= Game.MIN_WIDTH) {
      // Wide enough (every desktop, and any phone held in landscape): fill the viewport.
      Game.offsetX = 0;
      Game.offsetY = 0;
    } else {
      // Narrower than the UI can survive. The whole interface is laid out against a ~1280
      // logical width — the Customize preview panel, the coin-shop grid, the back button at
      // WIDTH-125 — so letting WIDTH fall to a portrait phone's ~333 would not "reflow", it
      // would overlap everything into garbage. Instead pin the logical width and scale to fit,
      // letterboxing top and bottom. Portrait also gets a rotate prompt; this is the fallback
      // for anyone who ignores it, and for tablets where the strip is still playable.
      logicalWidth = Game.MIN_WIDTH;
      Game.scale = Game.windowWidth / Game.MIN_WIDTH;
      Game.offsetX = 0;
      Game.offsetY = Math.trunc((Game.windowHeight - Game.HEIGHT * Game.scale) / 2);
    }

    Game.WIDTH = logicalWidth;

    // Anything cached at the old logical width is now stale.
    PageRenderer.invalidateCache();
    this.geoCache = null;
  }

  static toGameX(screenX: number): number {
    return Math.trunc((screenX - Game.offsetX) / Game.scale);
  }

  static toGameY(screenY: number): number {
    return Math.trunc((screenY - Game.offsetY) / Game.scale);
  }

  static getScale(): number {
    return Game.scale;
  }

  /** True when the touch control scheme is live — see Device.isTouchDevice(). */
  static isTouchInput(): boolean {
    return Game.instance !== null && Game.instance.isTouch;
  }

  /**
   * Logical -> client (CSS) coordinates: the exact inverse of canvasPos + toGameX/toGameY.
   * Used by the device harness so it doesn't have to re-derive the DPR cap and letterbox
   * offsets and get them subtly wrong.
   */
  clientFromLogical(lx: number, ly: number): { x: number; y: number } {
    const rect = this.canvas.getBoundingClientRect();
    const ratio = this.canvas.width / rect.width; // device px per CSS px
    return {
      x: rect.left + (lx * Game.scale + Game.offsetX) / ratio,
      y: rect.top + (ly * Game.scale + Game.offsetY) / ratio,
    };
  }

  // ---- input wiring ----

  private canvasPos(e: { clientX: number; clientY: number }): { x: number; y: number } {
    const rect = this.canvas.getBoundingClientRect();
    const dpr = this.canvas.width / rect.width;
    return {
      x: (e.clientX - rect.left) * dpr,
      y: (e.clientY - rect.top) * dpr,
    };
  }

  // ---- Touch Events ----
  //
  // Touch is driven by Touch Events, not Pointer Events, even though Pointer Events are the
  // modern API and work fine in Chrome's device emulation. On a real iPhone the game works in
  // Safari but the joystick is dead in Chrome — and Chrome on iOS is the same WebKit engine, so
  // the engine is not the difference. What differs is that Chrome iOS claims touch drags for its
  // own UI gestures (toolbar collapse, swipe-back) and stops delivering the pointer stream
  // mid-drag. Touch Events are not subject to that and behave identically across every mobile
  // browser, so they are the reliable substrate here. Pointer Events still drive mouse input.

  private attachTouchListeners(): void {
    const canvas = this.canvas;

    const toLogical = (t: Touch) => {
      const { x, y } = this.canvasPos(t);
      return { gx: Game.toGameX(x), gy: Game.toGameY(y), x, y };
    };

    // Listen on window, not the canvas. If the UA retargets a touch away from the canvas
    // mid-gesture (Chrome iOS does this when it decides the drag belongs to its own UI), a
    // canvas-bound listener simply stops hearing about the finger. Coordinates are computed
    // against the canvas rect regardless of what the event's target happens to be.
    const target: EventTarget = window;

    // Touches that land on an overlay belong to the overlay, not the game.
    //
    // Keying this off "is the overlay currently visible" is NOT enough, and getting that wrong
    // is subtle: pointerdown fires BEFORE touchstart, so #boot's own pointerdown handler has
    // already hidden itself by the time our touchstart runs. A visibility check therefore sees
    // no overlay and hands the dismissal tap straight through to the menu underneath — the tap
    // that starts the game also clicks whatever button is behind it. Test the event's target.
    const fromOverlay = (ev: TouchEvent) => {
      const t = ev.target as Element | null;
      return !!(t && typeof t.closest === 'function' && (t.closest('#boot') || t.closest('#rotate')));
    };

    // A scroll gesture the UA starts is what fires touchcancel and kills the stick under the
    // player's thumb. touch-action:none is supposed to prevent that; Chrome iOS does not always
    // honour it. Killing the default on every touchmove is the belt to that brace.
    const swallow = (ev: TouchEvent) => {
      if (fromOverlay(ev)) return;
      if (ev.cancelable) ev.preventDefault();
    };
    document.addEventListener('touchmove', swallow, { passive: false });

    // preventDefault only when the UA will honour it: a non-cancelable touch event means the
    // browser has already committed to its own gesture, and calling it anyway just logs an error.
    target.addEventListener('touchstart', ((ev: TouchEvent) => {
      if (fromOverlay(ev)) return;
      if (ev.cancelable) ev.preventDefault(); // stop scroll, zoom, and the synthetic mouse events
      canvas.focus();
      for (const t of Array.from(ev.changedTouches)) {
        const { gx, gy, x, y } = toLogical(t);
        TouchControls.debugLog('down', t.identifier, gx, gy, 'touch');
        if (this.touch.pointerDown(t.identifier, gx, gy)) continue;
        const e = new MouseEvent(x, y, MouseEvent.BUTTON1);
        if (this.beginTouchMenuGesture(t.identifier, x, y, e)) continue;
        this.menu.mousePressed(e);
        this.shop.mousePressed(e);
        this.musicPlayer.mousePressed(e);
      }
    }) as EventListener, { passive: false });

    target.addEventListener('touchmove', ((ev: TouchEvent) => {
      if (fromOverlay(ev)) return;
      if (ev.cancelable) ev.preventDefault();
      for (const t of Array.from(ev.changedTouches)) {
        const { gx, gy, x, y } = toLogical(t);
        TouchControls.debugLog('move', t.identifier, gx, gy, 'touch');
        if (this.touch.pointerMove(t.identifier, gx, gy)) continue;
        if (this.moveTouchMenuGesture(t.identifier, x, y)) continue;
        const e = new MouseEvent(x, y, MouseEvent.BUTTON1);
        this.menu.mouseDragged(e);
        this.shop.mouseDragged(e);
        this.musicPlayer.mouseDragged(e);
      }
    }) as EventListener, { passive: false });

    const endTouch = (ev: TouchEvent, cancelled: boolean) => {
      for (const t of Array.from(ev.changedTouches)) {
        const { x, y, gx, gy } = toLogical(t);
        TouchControls.debugLog(cancelled ? 'CANCEL' : 'up', t.identifier, gx, gy, 'touch');
        const e = new MouseEvent(x, y, MouseEvent.BUTTON1);
        this.touch.pointerUp(t.identifier);
        if (cancelled) {
          this.cancelTouchMenuGesture(t.identifier);
        } else if (!this.endTouchMenuGesture(t.identifier, e)) {
          this.menu.mouseReleased(e);
          this.musicPlayer.mouseReleased(e);
        }
      }
    };

    target.addEventListener('touchend', ((ev: TouchEvent) => {
      if (fromOverlay(ev)) return;
      if (ev.cancelable) ev.preventDefault();
      endTouch(ev, false);
    }) as EventListener, { passive: false });
    target.addEventListener('touchcancel', ((ev: TouchEvent) => {
      if (fromOverlay(ev)) return;
      endTouch(ev, true);
    }) as EventListener);
  }

  // ---- touch gestures on menu pages ----
  //
  // On a scrollable page a tap and the start of a scroll drag look identical at pointer-down.
  // Firing the click there would mean that dragging to scroll the Coin Shop buys an item. So
  // on touch we defer the click to pointer-UP and only fire it if the finger barely moved.
  // Sliders are the exception — their drag has to begin on pointer-down — so we detect those
  // up front and pass them straight through.

  private static readonly TAP_SLOP = 12; // logical px

  private touchGesture: {
    id: number;
    downEvent: MouseEvent;
    downX: number;
    downY: number;
    lastY: number;
    moved: boolean;
    slider: boolean;
  } | null = null;

  /** @returns true if this pointer is now owned by a menu gesture. */
  private beginTouchMenuGesture(id: number, x: number, y: number, e: MouseEvent): boolean {
    const state = Game.gameState as STATE;
    if (!Menu.isScrollableState(state)) return false; // non-scrolling page: click immediately

    // One gesture at a time. A second finger landing mid-scroll must not replace the live one:
    // it would install itself as a fresh, unmoved gesture and fire its click on release —
    // i.e. two-thumb scrolling the Coin Shop would buy whatever the second thumb rested on.
    // Swallow it instead, so it can't reach the menu by any route either.
    if (this.touchGesture) return true;

    const gx = Game.toGameX(x);
    const gy = Game.toGameY(y);
    const slider = this.menu.hitsAnySlider(gx, gy);

    this.touchGesture = {
      id, downEvent: e, downX: gx, downY: gy, lastY: gy, moved: false, slider,
    };

    if (slider) this.menu.mousePressed(e); // a slider's drag must begin now
    return true;
  }

  private moveTouchMenuGesture(id: number, x: number, y: number): boolean {
    const g = this.touchGesture;
    if (!g) return false;
    if (g.id !== id) return true; // a second finger while a gesture is live: ignore it

    const gx = Game.toGameX(x);
    const gy = Game.toGameY(y);

    if (g.slider) {
      this.menu.mouseDragged(new MouseEvent(x, y, MouseEvent.BUTTON1));
      return true;
    }

    // Slop is measured from the DOWN point, not per-event: a slow drag would never trip a
    // per-delta threshold and would still be delivered as a click on release.
    const dx = gx - g.downX;
    const dy = gy - g.downY;
    if (!g.moved && dx * dx + dy * dy > Game.TAP_SLOP * Game.TAP_SLOP) g.moved = true;

    // Dragging the content up scrolls down — same sense as a wheel notch.
    this.menu.scrollBy(-(gy - g.lastY));
    g.lastY = gy;
    return true;
  }

  /**
   * The gesture died without a pointerup — the system took the touch (notification shade,
   * incoming call, app switch). Dropping it is not enough for a slider: Menu.settingsDrag only
   * clears in mouseReleased, so leaving it set latches the volume slider onto whatever pointer
   * moves next, and keeps it drawn in its enlarged dragging state. No click is delivered — a
   * cancelled tap is not a tap. `id` is null when the window lost focus and every gesture is void.
   */
  private cancelTouchMenuGesture(id: number | null): void {
    const g = this.touchGesture;
    if (!g || (id !== null && g.id !== id)) return;
    this.touchGesture = null;
    if (g.slider) this.menu.mouseReleased(new MouseEvent(0, 0, MouseEvent.BUTTON1));
  }

  private endTouchMenuGesture(id: number, e: MouseEvent): boolean {
    const g = this.touchGesture;
    if (!g) return false;
    if (g.id !== id) return true; // the swallowed second finger lifting: nothing to deliver
    this.touchGesture = null;

    if (g.slider) {
      this.menu.mouseReleased(e);
      return true;
    }
    if (!g.moved) {
      // It was a tap, not a scroll — deliver the click now.
      this.menu.mousePressed(g.downEvent);
      this.menu.mouseReleased(e);
    }
    return true;
  }

  /** AWT reports button 1/2/3; DOM reports 0/1/2. */
  private static awtButton(domButton: number): number {
    return domButton === 0 ? MouseEvent.BUTTON1 : domButton === 1 ? MouseEvent.BUTTON2 : MouseEvent.BUTTON3;
  }

  private attachListeners(): void {
    const canvas = this.canvas;

    if (this.isTouch) this.attachTouchListeners();

    canvas.addEventListener('pointerdown', (ev) => {
      // Touch is handled by the Touch Events path; ignoring touch-type pointers here
      // prevents every finger from being processed twice.
      if (this.isTouch && ev.pointerType === 'touch') return;
      ev.preventDefault();
      canvas.focus();
      // setPointerCapture throws NotFoundError if the UA considers the pointer already gone.
      // Unguarded, that exception aborts the REST of this handler — so every control on the
      // screen would silently stop working. Never let it take the input system down with it.
      try {
        canvas.setPointerCapture?.(ev.pointerId);
      } catch {
        // Capture is an optimisation, not a requirement; touch has implicit capture anyway.
      }
      const { x, y } = this.canvasPos(ev);
      const e = new MouseEvent(x, y, Game.awtButton(ev.button));

      TouchControls.debugLog('down', ev.pointerId, Game.toGameX(x), Game.toGameY(y), ev.pointerType);

      this.menu.mousePressed(e);
      this.shop.mousePressed(e);
      this.musicPlayer.mousePressed(e);
    });

    canvas.addEventListener('pointerup', (ev) => {
      // Touch is handled by the Touch Events path; ignoring touch-type pointers here
      // prevents every finger from being processed twice.
      if (this.isTouch && ev.pointerType === 'touch') return;
      const { x, y } = this.canvasPos(ev);
      const e = new MouseEvent(x, y, Game.awtButton(ev.button));

      TouchControls.debugLog('up', ev.pointerId, Game.toGameX(x), Game.toGameY(y), ev.pointerType);

      this.menu.mouseReleased(e);
      this.musicPlayer.mouseReleased(e);
    });

    canvas.addEventListener('pointermove', (ev) => {
      // Touch is handled by the Touch Events path; ignoring touch-type pointers here
      // prevents every finger from being processed twice.
      if (this.isTouch && ev.pointerType === 'touch') return;
      const { x, y } = this.canvasPos(ev);

      TouchControls.debugLog('move', ev.pointerId, Game.toGameX(x), Game.toGameY(y), ev.pointerType);

      // AWT fires mouseDragged while a button is held and mouseMoved otherwise.
      const dragging = ev.buttons !== 0;
      const e = new MouseEvent(x, y, dragging ? MouseEvent.BUTTON1 : 0);
      if (dragging) {
        this.menu.mouseDragged(e);
        this.shop.mouseDragged(e);
        this.musicPlayer.mouseDragged(e);
      } else {
        this.menu.mouseMoved(e);
        this.shop.mouseMoved(e);
        this.musicPlayer.mouseMoved(e);
      }
      // Combat mode aims at the cursor. On touch, TouchControls owns aiming instead.
      if (!this.isTouch) {
        Game.mouseGameX = Game.toGameX(x);
        Game.mouseGameY = Game.toGameY(y);
      }
    });

    canvas.addEventListener('pointercancel', (ev) => {
      // Touch is handled by the Touch Events path; ignoring touch-type pointers here
      // prevents every finger from being processed twice.
      if (this.isTouch && ev.pointerType === 'touch') return;
      // If a real device fires this mid-drag (iOS does it liberally), the joystick dies under
      // the player's thumb. The diagnostic overlay surfaces it rather than leaving it invisible.
      TouchControls.debugLog('CANCEL', ev.pointerId, 0, 0, ev.pointerType);
    });

    canvas.addEventListener('wheel', (ev) => {
      ev.preventDefault();
      const { x, y } = this.canvasPos(ev);
      // AWT's wheel rotation is in notches; DOM gives pixels/lines. Normalize to ±1 per notch.
      const rotation = ev.deltaY > 0 ? 1 : ev.deltaY < 0 ? -1 : 0;
      this.menu.mouseWheelMoved(new MouseWheelEvent(x, y, rotation));
    }, { passive: false });

    canvas.addEventListener('contextmenu', (ev) => ev.preventDefault());

    window.addEventListener('keydown', (ev) => {
      const vk = domKeyToVK(ev);
      if (vk < 0) return;
      // Space/arrows scroll the page otherwise.
      ev.preventDefault();
      if (ev.repeat) return;
      this.keyInput.keyPressed(new KeyEvent(vk));
    });

    window.addEventListener('keyup', (ev) => {
      const vk = domKeyToVK(ev);
      if (vk < 0) return;
      ev.preventDefault();
      this.keyInput.keyReleased(new KeyEvent(vk));
    });

    // Releasing the mouse outside the canvas must still stop combat-mode fire.
    // Mouse only: on touch this is multi-touch-hostile — lifting the movement thumb also fires
    // a window pointerup, which would cancel fire while a finger is still holding FIRE.
    // TouchControls owns mouseShootHeld on touch devices and releases it per-pointer.
    window.addEventListener('pointerup', () => {
      if (this.isTouch) return;
      Game.mouseShootHeld = false;
    });

    // Losing focus mid-run would otherwise leave movement keys (or a thumb) stuck down.
    window.addEventListener('blur', () => {
      this.keyInput.releaseAll();
      this.touch.releaseAll();
      this.cancelTouchMenuGesture(null);
      Game.mouseShootHeld = false;
    });

    let resizeTimer = 0;
    window.addEventListener('resize', () => {
      window.clearTimeout(resizeTimer);
      resizeTimer = window.setTimeout(() => this.calculateWindowSize(), 120);
    });
  }

  // ---- main loop ----

  start(): void {
    if (this.running) return;
    this.running = true;

    const NS_PER_TICK = 1000 / 60; // ms
    let lastTime = performance.now();
    let delta = 0;
    let timer = Date.now();
    let frames = 0;

    const loop = (now: number) => {
      if (!this.running) return;
      requestAnimationFrame(loop);

      delta += (now - lastTime) / NS_PER_TICK;
      lastTime = now;

      // A backgrounded tab can accumulate thousands of ticks; cap the catch-up so the
      // game resumes rather than freezing while it simulates the missing minutes.
      if (delta > 5) delta = 5;

      while (delta >= 1) {
        this.tick();
        delta--;
      }

      this.render();
      frames++;

      if (Date.now() - timer > 1000) {
        timer += 1000;
        this.fps = frames;
        frames = 0;
      }
    };

    requestAnimationFrame(loop);
  }

  stop(): void {
    this.running = false;
  }

  // ---- tick ----

  private tick(): void {
    // Auto-advance music — runs in every state
    AudioPlayer.checkAutoAdvance();

    if (Game.gameState === STATE.Paused) {
      this.menu.tick(); // for hover animations
      return;
    }

    // Decay screen effects
    if (Game.shakeIntensity > 0.1) Game.shakeIntensity *= 0.85;
    else Game.shakeIntensity = 0;
    if (Game.flashAlpha > 0.01) Game.flashAlpha *= 0.88;
    else Game.flashAlpha = 0;
    if (Game.levelUpFlash > 0.01) Game.levelUpFlash *= 0.93;
    else Game.levelUpFlash = 0;
    if (Game.attemptFade > 0.005) Game.attemptFade *= 0.985;
    else Game.attemptFade = 0;

    // Animate enemy explosion particles (works for boss intro AND regular clears)
    if (Game.enemyExplosions !== null) {
      let anyAlive = false;
      for (const p of Game.enemyExplosions) {
        if (p[8] > 0) {
          p[0] += p[2];
          p[1] += p[3];
          p[3] += 0.12; // gravity
          p[2] *= 0.97;
          p[8] -= 0.02;
          anyAlive = true;
        }
      }
      if (!anyAlive && !Game.bossIntroActive) {
        Game.enemyExplosions = null;
      }
    }

    // Bullet cascade — sequential explosions
    if (Game.cascadeActive && Game.bulletCascade !== null) {
      Game.cascadeTick++;
      let anyLeft = false;
      const cr = Game.shakeRng;
      for (const b of Game.bulletCascade) {
        if (b[3] === 0) {
          if (Game.cascadeTick >= b[2]) {
            b[3] = 1;
            Game.shakeIntensity = Math.max(Game.shakeIntensity, 3);
            const count = 4 + cr.nextInt(3);
            for (let j = 0; j < count && Game.cascadeParticleCount < Game.cascadeParticles.length; j++) {
              const angle = cr.nextFloat() * Math.PI * 2;
              const speed = 2 + cr.nextFloat() * 4;
              Game.cascadeParticles[Game.cascadeParticleCount] = [
                b[0], b[1],
                Math.cos(angle) * speed, Math.sin(angle) * speed,
                0.8, 235, 87, 87, 2 + cr.nextFloat() * 4,
              ];
              Game.cascadeParticleCount++;
            }
          } else {
            anyLeft = true;
          }
        }
      }
      if (!anyLeft && Game.cascadeParticleCount === 0) {
        Game.cascadeActive = false;
        Game.bulletCascade = null;
      }
    }

    // Cascade particle physics
    if (Game.cascadeParticleCount > 0) {
      let cpAlive = 0;
      for (let i = 0; i < Game.cascadeParticleCount; i++) {
        const p = Game.cascadeParticles[i];
        p[0] += p[2];
        p[1] += p[3];
        p[2] *= 0.93;
        p[3] *= 0.93;
        p[3] += 0.08;
        p[4] -= 0.025;
        if (p[4] > 0) {
          Game.cascadeParticles[cpAlive] = p;
          cpAlive++;
        }
      }
      Game.cascadeParticleCount = cpAlive;
    }

    // Boss intro cinematic
    if (Game.bossIntroActive) {
      Game.bossIntroTimer++;
      if (Game.bossIntroTimer < 100) {
        Game.shakeIntensity = Math.max(Game.shakeIntensity, 2 + Game.bossIntroTimer * 0.06);
      }
      if (Game.bossIntroTimer === 100) {
        Game.shakeIntensity = 16;
      }
      if (Game.bossIntroTimer >= 130) {
        Game.bossIntroActive = false;
        Game.enemyExplosions = null;
      }
    }

    // Screen transitions — detect state changes
    // The Paused early-return above lets TS prove `gameState !== Paused` here, so it flags
    // the Java's literal condition as dead code. Widen back to the full enum rather than
    // delete the check, so this still reads 1:1 against Game.java.
    const gs = Game.gameState as STATE;
    if (gs !== this.lastState) {
      // Skip transitions for states that have their own animations
      if (this.lastState !== STATE.Dying && gs !== STATE.Dying
          && gs !== STATE.Paused && this.lastState !== STATE.Paused) {
        this.transitionAlpha = 1;
        // Zoom-in effect when entering gameplay
        if (gs === STATE.Game && this.lastState !== STATE.Shop) {
          this.transitionZoom = 1;
        }
      }
      this.lastState = gs;
    }
    if (this.transitionAlpha > 0.01) this.transitionAlpha *= (1 - Game.TRANS_SPEED);
    else this.transitionAlpha = 0;
    if (this.transitionZoom > 0.01) this.transitionZoom *= 0.90;
    else this.transitionZoom = 0;

    // Geometric layer animation — faster on harder difficulties
    this.geoPhase += 0.008 * GamePalette.getGeoSpeed();

    // Wall pulse + flare decay
    Game.wallPulsePhase += 0.04;
    let alive = 0;
    for (let i = 0; i < Game.wallFlareCount; i++) {
      Game.wallFlares[i][2] *= 0.88;
      if (Game.wallFlares[i][2] > 0.02) {
        Game.wallFlares[alive] = Game.wallFlares[i];
        alive++;
      }
    }
    Game.wallFlareCount = alive;

    // Bounce particle decay
    let bpAlive = 0;
    for (let i = 0; i < Game.bounceParticleCount; i++) {
      const p = Game.bounceParticles[i];
      p[0] += p[2];
      p[1] += p[3]; // move
      p[2] *= 0.94;
      p[3] *= 0.94; // drag
      p[4] -= 0.03; // fade
      if (p[4] > 0) {
        Game.bounceParticles[bpAlive] = p;
        bpAlive++;
      }
    }
    Game.bounceParticleCount = bpAlive;

    // Smooth time scale transition
    Game.timeScale += (Game.timeScaleTarget - Game.timeScale) * 0.15;
    if (Math.abs(Game.timeScale - Game.timeScaleTarget) < 0.01) Game.timeScale = Game.timeScaleTarget;

    if (Game.gameState === STATE.Game) {
      GamePalette.update(this.hud.getLevel());
      const objects = this.handler.getObjects();

      // Player always ticks at full speed (responsive controls)
      for (let i = 0; i < objects.length; i++) {
        const obj = objects[i];
        if (obj instanceof Player) {
          obj.tick();
        }
      }

      // Everything else ticks at time scale rate
      this.slowmoAccum += Game.timeScale;
      if (this.slowmoAccum >= 1) {
        this.slowmoAccum -= 1;
        this.hud.tick();
        this.spawner.tick();
        // Tick non-player objects
        const objs2 = this.handler.getObjects();
        for (let i = 0; i < objs2.length; i++) {
          const obj = objs2[i];
          if (!(obj instanceof Player)) {
            obj.tick();
          }
        }
        this.handler.flushRemoves();
      }

      // Track player velocity for speed lines
      let enemyCount = 0;
      const objs3 = this.handler.getObjects();
      for (let i = 0; i < objs3.length; i++) {
        const obj = objs3[i];
        if (obj instanceof Player) {
          this.playerVelX = obj.getVelX();
          this.playerVelY = obj.getVelY();
          this.playerSpeed = Math.sqrt(this.playerVelX * this.playerVelX + this.playerVelY * this.playerVelY);
        }
        const id = obj.getId();
        if (id === ID.BasicEnemy || id === ID.FastEnemy || id === ID.SmartEnemy
            || id === ID.HardEnemy || id === ID.EnemyBoss) {
          enemyCount++;
        }
      }

      // Camera zoom — subtle zoom-out as enemies accumulate
      this.cameraZoomTarget = 1 - Math.min(enemyCount, 8) * 0.008; // max ~6.4% zoom out
      this.cameraZoom += (this.cameraZoomTarget - this.cameraZoom) * 0.03;

      // Look-ahead camera offset — drifts toward movement direction
      let targetOffX = 0;
      let targetOffY = 0;
      if (this.playerSpeed > 1) {
        const norm = Math.min(this.playerSpeed / 8, 1);
        targetOffX = (this.playerVelX / this.playerSpeed) * Game.CAM_LOOK_AHEAD * norm;
        targetOffY = (this.playerVelY / this.playerSpeed) * Game.CAM_LOOK_AHEAD * norm;
      }
      this.camOffsetX += (targetOffX - this.camOffsetX) * Game.CAM_LERP;
      this.camOffsetY += (targetOffY - this.camOffsetY) * Game.CAM_LERP;

      // Spawn speed lines based on player movement
      this.updateSpeedLines();

      if (HUD.HEALTH <= 0) {
        this.onDeath(objs3);
      }
    } else if (Game.gameState === STATE.Dying) {
      this.deathTimer++;

      // Freeze for first 12 frames (200ms), then animate particles
      if (this.deathTimer > 12 && this.deathParticles !== null) {
        for (const p of this.deathParticles) {
          p[0] += p[2]; // x += vx
          p[1] += p[3]; // y += vy
          p[3] += 0.15; // gravity
          p[2] *= 0.98; // drag
          p[4] *= 0.985; // shrink
        }
      }

      // White flash decay
      if (this.deathFlash > 0.01) this.deathFlash *= 0.92;
      else this.deathFlash = 0;

      // Transition to End screen after ~120 frames (2 seconds)
      if (this.deathTimer >= 120) {
        HUD.HEALTH = 100;
        this.hud.bounds = 0;
        Game.gameState = STATE.End;
        this.handler.clear();
        for (let i = 0; i < 15; i++) {
          this.handler.addObject(new MenuParticle(this.r.nextInt(Game.WIDTH), this.r.nextInt(Game.HEIGHT), ID.MenuParticle, this.handler));
        }
        this.deathParticles = null;
      }
    } else if (Game.gameState === STATE.Shop) {
      // shop handles its own input via mouse listener
    } else if (Game.gameState === STATE.MusicPlayer) {
      this.musicPlayer.tick();
    } else if (Game.gameState === STATE.Settings || Game.gameState === STATE.Statistics
        || Game.gameState === STATE.AchievementsPage || Game.gameState === STATE.Customize
        || Game.gameState === STATE.Loadout || Game.gameState === STATE.CoinShopPage
        || Game.gameState === STATE.DailyPage) {
      this.menu.tick();
    } else {
      this.menu.tick();
      this.handler.tick();
    }

    if (this.isTouch) this.touch.tick();

    TrailPool.tick();
  }

  /** Extracted from tick() for readability; body is Game.java's HEALTH<=0 branch verbatim. */
  private onDeath(objects: GameObject[]): void {
    // Capture all run stats
    this.lastScore = this.hud.getScore();
    this.lastLevel = this.hud.getLevel();
    this.lastTime = this.hud.getTimeSurvived();
    this.lastUpgrades = this.hud.getTotalUpgrades();
    this.lastHealthUps = this.hud.getHealthUpgrades();
    this.lastSpeedUps = this.hud.getSpeedUpgrades();
    this.lastRefills = this.hud.getRefills();
    this.lastDifficulty = this.diff === 0 ? 'Normal' : this.diff === 1 ? 'Hard' : this.diff === 2 ? 'Insane' : 'Combat';
    this.lastEnemiesKilled = this.runEnemiesKilled;
    this.lastIsHighScore = Profile.submitScore(this.diff, this.lastScore);

    this.lastEnemies = 0;
    for (let i = 0; i < objects.length; i++) {
      const obj = objects[i];
      const id = obj.getId();
      if (id === ID.BasicEnemy || id === ID.FastEnemy || id === ID.SmartEnemy
          || id === ID.HardEnemy || id === ID.EnemyBoss) {
        this.lastEnemies++;
      }
      // Count enemy encounters for profile
      if (id === ID.BasicEnemy && !(obj instanceof EnemyBossBullet)) this.runEncBasic++;
      else if (id === ID.FastEnemy) this.runEncFast++;
      else if (id === ID.SmartEnemy) this.runEncSmart++;
      else if (id === ID.HardEnemy) this.runEncHard++;
      else if (id === ID.EnemyBoss) this.runEncBoss++;
    }

    // Track longest streak from player
    for (let i = 0; i < objects.length; i++) {
      const obj = objects[i];
      if (obj instanceof Player) {
        const streak = obj.getStreakTicks();
        if (streak > this.runLongestStreak) this.runLongestStreak = streak;
      }
    }

    // Submit to Profile
    this.lastXpEarned = Profile.endRun(this.diff, this.lastScore, this.lastLevel,
      this.hud.getTicksSurvived(), this.lastHealthUps, this.lastSpeedUps, this.lastRefills,
      this.runLongestStreak, this.runBossesDefeated, this.runDamageTaken,
      this.runEncBasic, this.runEncFast, this.runEncSmart, this.runEncHard, this.runEncBoss);
    this.lastLeveledUp = Profile.didLevelUp();
    this.lastProfileLevel = Profile.getLevel();
    this.lastCoinsEarned = Profile.getRunCoinsEarned();

    // Submit daily score if in daily mode
    if (Game.dailyMode) {
      DailyChallenge.submitScore(this.lastScore);
      Game.dailyMode = false;
    }

    // Check achievements
    Achievements.checkAfterRun(this.lastScore, this.lastLevel, this.hud.getTicksSurvived(),
      this.lastUpgrades, this.lastHealthUps, this.lastSpeedUps,
      this.runBossesDefeated, this.runDamageTaken,
      this.runLongestStreak, this.diff, this.hud.getWaveCount());

    // Find player position for death particles
    for (let i = 0; i < objects.length; i++) {
      const obj = objects[i];
      if (obj.getId() === ID.Player) {
        this.deathPlayerX = obj.getX() + 24;
        this.deathPlayerY = obj.getY() + 24;
        break;
      }
    }

    // Spawn death particles
    const count = 25 + this.r.nextInt(10);
    this.deathParticles = Array.from({ length: count }, () => new Array<number>(8).fill(0));
    for (let i = 0; i < count; i++) {
      const angle = this.r.nextFloat() * Math.PI * 2;
      const speed = 2 + this.r.nextFloat() * 6;
      const size = 3 + this.r.nextFloat() * 8;
      // White/teal fragments
      const tint = this.r.nextFloat();
      this.deathParticles[i] = [
        this.deathPlayerX, this.deathPlayerY,
        Math.cos(angle) * speed, Math.sin(angle) * speed,
        size,
        200 + Math.trunc(55 * tint), 220 + Math.trunc(35 * tint), 230 + Math.trunc(25 * tint),
      ];
    }

    // Remove player from world
    this.handler.removeIf((obj) => obj.getId() === ID.Player);

    this.deathTimer = 0;
    this.deathFlash = 1;
    Game.shakeIntensity = 14;
    Game.gameState = STATE.Dying;
  }

  // ---- render ----

  private render(): void {
    const g = this.g;
    g.resetFrame();

    // Clear at native resolution before scaling
    g.setColor(Color.black);
    g.fillRect(0, 0, this.canvas.width, this.canvas.height);

    // Letterbox: centre the 16:9 frame when the viewport is too narrow to fill (portrait
    // phones, tall tablets). Zero on every normal landscape screen.
    if (Game.offsetX !== 0 || Game.offsetY !== 0) {
      g.translate(Game.offsetX, Game.offsetY);
    }

    // Scale — everything below is in logical coordinates (WIDTHxHEIGHT)
    g.scale(Game.scale, Game.scale);
    g.setRenderingHint(RenderingHints.KEY_INTERPOLATION, RenderingHints.VALUE_INTERPOLATION_BILINEAR);

    const WIDTH = Game.WIDTH;
    const HEIGHT = Game.HEIGHT;

    // When letterboxed, the canvas is bigger than the frame, so the black bars would catch
    // everything the renderer deliberately draws outside the arena (death particles falling
    // under gravity, speed lines spawned past the edge, shake-displaced effects). Without
    // bars the canvas edge is the frame edge and clips them for free — so only clip here.
    const letterboxed = Game.offsetX !== 0 || Game.offsetY !== 0;
    if (letterboxed) {
      g.clipRect(0, 0, WIDTH, HEIGHT);
    }

    // Transition zoom — slight zoom-in when entering game
    const hasTransZoom = this.transitionZoom > 0.02;
    if (hasTransZoom) {
      const z = 1 + this.transitionZoom * 0.06;
      g.translate(WIDTH * (1 - z) / 2, HEIGHT * (1 - z) / 2);
      g.scale(z, z);
    }

    if (Game.gameState === STATE.Game || Game.gameState === STATE.Shop
        || Game.gameState === STATE.Paused || Game.gameState === STATE.Dying) {
      const beat = AudioPlayer.getBeatPulse();
      PageRenderer.drawGameBackground(g);
      this.renderBeatVisuals(g, beat);
      this.renderGeoLayers(g);
      this.renderNeonWalls(g, beat);
    }

    if (Game.gameState === STATE.Paused) {
      // Draw frozen game underneath
      TrailPool.render(g);
      this.handler.render(g);
      this.hud.render(g);
      // Dim overlay
      g.setColor(Game.DYING_OVERLAY);
      g.fillRect(0, 0, WIDTH, HEIGHT);
      // Pause menu rendered by Menu
      this.menu.render(g);
    } else if (Game.gameState === STATE.Dying) {
      // Screen shake applies to frozen world
      let dsx = 0;
      let dsy = 0;
      const shakeAmount = Game.shakeIntensity * Settings.getShakeMultiplier();
      if (shakeAmount > 0) {
        dsx = (Game.shakeRng.nextFloat() - 0.5) * 2 * shakeAmount;
        dsy = (Game.shakeRng.nextFloat() - 0.5) * 2 * shakeAmount;
        g.translate(dsx, dsy);
      }

      // Render frozen game world (enemies still visible, player removed)
      TrailPool.render(g);
      this.handler.render(g);

      if (shakeAmount > 0) g.translate(-dsx, -dsy);

      // Death particles
      if (this.deathParticles !== null) {
        g.setRenderingHint(RenderingHints.KEY_ANTIALIASING, RenderingHints.VALUE_ANTIALIAS_ON);
        const fade = Math.max(0, 1 - this.deathTimer / 100);
        for (const p of this.deathParticles) {
          if (p[4] < 0.5) continue;
          const alpha = Math.trunc(fade * 255);
          g.setColor(new Color(Math.trunc(p[5]), Math.trunc(p[6]), Math.trunc(p[7]), Math.max(0, Math.min(255, alpha))));
          const s = Math.trunc(p[4]);
          g.fillRoundRect(Math.trunc(p[0]) - Math.trunc(s / 2), Math.trunc(p[1]) - Math.trunc(s / 2), s, s, 3, 3);
        }
      }

      // White flash overlay
      if (this.deathFlash > 0) {
        g.setColor(new Color(255, 255, 255, Math.trunc(this.deathFlash * 200)));
        g.fillRect(0, 0, WIDTH, HEIGHT);
      }

      // Fade to black as we approach transition
      if (this.deathTimer > 80) {
        const blackFade = (this.deathTimer - 80) / 40;
        g.setColor(new Color(10, 12, 18, Math.trunc(Math.min(1, blackFade) * 255)));
        g.fillRect(0, 0, WIDTH, HEIGHT);
      }

      this.hud.render(g);
    } else if (Game.gameState === STATE.Game) {
      // Camera zoom — subtle zoom-out with enemy count
      if (this.cameraZoom !== 1) {
        const zoomOffX = WIDTH * (1 - this.cameraZoom) / 2;
        const zoomOffY = HEIGHT * (1 - this.cameraZoom) / 2;
        g.translate(zoomOffX, zoomOffY);
        g.scale(this.cameraZoom, this.cameraZoom);
      }

      // Look-ahead camera drift
      g.translate(-this.camOffsetX, -this.camOffsetY);

      // Screen shake — offset the game world
      let sx = 0;
      let sy = 0;
      const shakeAmount = Game.shakeIntensity * Settings.getShakeMultiplier();
      if (shakeAmount > 0) {
        sx = (Game.shakeRng.nextFloat() - 0.5) * 2 * shakeAmount;
        sy = (Game.shakeRng.nextFloat() - 0.5) * 2 * shakeAmount;
        g.translate(sx, sy);
      }
      TrailPool.render(g);
      this.handler.render(g);
      if (shakeAmount > 0) {
        g.translate(-sx, -sy);
      }

      // Speed lines
      this.renderSpeedLines(g);

      // Enemy clear explosions (non-boss-intro)
      if (Game.enemyExplosions !== null && !Game.bossIntroActive) {
        for (const p of Game.enemyExplosions) {
          if (p[8] <= 0) continue;
          const ea = Math.min(255, Math.trunc(p[8] * 255));
          g.setColor(new Color(Math.trunc(p[5]), Math.trunc(p[6]), Math.trunc(p[7]), ea));
          const es = Math.trunc(p[4]);
          g.fillRoundRect(Math.trunc(p[0]) - Math.trunc(es / 2), Math.trunc(p[1]) - Math.trunc(es / 2), es, es, 3, 3);
        }
      }

      // Bullet cascade particles
      if (Game.cascadeParticleCount > 0) {
        for (let i = 0; i < Game.cascadeParticleCount; i++) {
          const p = Game.cascadeParticles[i];
          if (p[4] <= 0) continue;
          const ca = Math.min(255, Math.trunc(p[4] * 255));
          g.setColor(new Color(Math.trunc(p[5]), Math.trunc(p[6]), Math.trunc(p[7]), ca));
          const cs = Math.max(1, Math.trunc(p[4] * 5));
          g.fillOval(Math.trunc(p[0]) - Math.trunc(cs / 2), Math.trunc(p[1]) - Math.trunc(cs / 2), cs, cs);
        }
      }

      // Unexploded bullets waiting in cascade — render as pulsing dots
      if (Game.cascadeActive && Game.bulletCascade !== null) {
        const pulse = Math.sin(Game.cascadeTick * 0.3) * 0.4 + 0.6;
        for (const b of Game.bulletCascade) {
          if (b[3] === 0) {
            const ba = Math.trunc(pulse * 180);
            g.setColor(new Color(235, 87, 87, ba));
            g.fillOval(Math.trunc(b[0]) - 5, Math.trunc(b[1]) - 5, 10, 10);
            g.setColor(new Color(255, 150, 150, Math.trunc(pulse * 60)));
            g.fillOval(Math.trunc(b[0]) - 8, Math.trunc(b[1]) - 8, 16, 16);
          }
        }
      }

      // --- Temporarily undo camera transforms for screen-edge effects ---
      g.translate(this.camOffsetX, this.camOffsetY);
      if (this.cameraZoom !== 1) {
        g.scale(1 / this.cameraZoom, 1 / this.cameraZoom);
        const uzx = WIDTH * (1 - this.cameraZoom) / 2;
        const uzy = HEIGHT * (1 - this.cameraZoom) / 2;
        g.translate(-uzx, -uzy);
      }

      // Damage flash — red border vignette (screen-space)
      if (Game.flashAlpha > 0) {
        const border = 60;
        const alpha = Math.trunc(Game.flashAlpha * 255);
        const dmgAlpha = new Color(235, 50, 50, alpha);
        g.setPaint(new GradientPaint(0, 0, dmgAlpha, 0, border, Game.DMG_RED_CLEAR));
        g.fillRect(0, 0, WIDTH, border);
        g.setPaint(new GradientPaint(0, HEIGHT - border, Game.DMG_RED_CLEAR, 0, HEIGHT, dmgAlpha));
        g.fillRect(0, HEIGHT - border, WIDTH, border);
        g.setPaint(new GradientPaint(0, 0, dmgAlpha, border, 0, Game.DMG_RED_CLEAR));
        g.fillRect(0, 0, border, HEIGHT);
        g.setPaint(new GradientPaint(WIDTH - border, 0, Game.DMG_RED_CLEAR, WIDTH, 0, dmgAlpha));
        g.fillRect(WIDTH - border, 0, border, HEIGHT);
      }

      // Boss intro cinematic overlay (screen-space)
      if (Game.bossIntroActive) {
        this.renderBossIntro(g);
      }

      // Level-up flash — teal border pulse (screen-space)
      if (Game.levelUpFlash > 0) {
        const border = 80;
        const alpha = Math.trunc(Game.levelUpFlash * 150);
        g.setPaint(new GradientPaint(0, 0, GamePalette.accent(alpha), 0, border, GamePalette.accent(0)));
        g.fillRect(0, 0, WIDTH, border);
        g.setPaint(new GradientPaint(0, HEIGHT - border, GamePalette.accent(0), 0, HEIGHT, GamePalette.accent(alpha)));
        g.fillRect(0, HEIGHT - border, WIDTH, border);
        g.setPaint(new GradientPaint(0, 0, GamePalette.accent(alpha), border, 0, GamePalette.accent(0)));
        g.fillRect(0, 0, border, HEIGHT);
        g.setPaint(new GradientPaint(WIDTH - border, 0, GamePalette.accent(0), WIDTH, 0, GamePalette.accent(alpha)));
        g.fillRect(WIDTH - border, 0, border, HEIGHT);
      }

      // --- Re-apply camera transforms for HUD ---
      if (this.cameraZoom !== 1) {
        const rzx = WIDTH * (1 - this.cameraZoom) / 2;
        const rzy = HEIGHT * (1 - this.cameraZoom) / 2;
        g.translate(rzx, rzy);
        g.scale(this.cameraZoom, this.cameraZoom);
      }
      g.translate(-this.camOffsetX, -this.camOffsetY);

      this.hud.render(g);

      // Attempt counter fade-in at start of run
      if (Game.attemptFade > 0.05) {
        const aAlpha = Math.trunc(Game.attemptFade * 200);
        const drift = (1 - Game.attemptFade) * 15;
        g.setFont(Game.ATTEMPT_FONT);
        g.setColor(new Color(140, 150, 165, Math.min(aAlpha, 255)));
        const aLabel = 'ATTEMPT';
        let afm = g.getFontMetrics();
        const ax = Math.trunc((WIDTH - afm.stringWidth(aLabel)) / 2);
        g.drawString(aLabel, ax, Math.trunc(HEIGHT / 2) + 60 + Math.trunc(drift));

        g.setFont(Game.ATTEMPT_NUM_FONT);
        g.setColor(new Color(230, 234, 240, Math.min(aAlpha, 255)));
        const aNum = '#' + Game.currentAttempt;
        afm = g.getFontMetrics();
        g.drawString(aNum, Math.trunc((WIDTH - afm.stringWidth(aNum)) / 2), Math.trunc(HEIGHT / 2) + 90 + Math.trunc(drift));
      }

      // Reset look-ahead + camera zoom transforms
      g.translate(this.camOffsetX, this.camOffsetY);
      if (this.cameraZoom !== 1) {
        g.scale(1 / this.cameraZoom, 1 / this.cameraZoom);
        const zoomOffX = WIDTH * (1 - this.cameraZoom) / 2;
        const zoomOffY = HEIGHT * (1 - this.cameraZoom) / 2;
        g.translate(-zoomOffX, -zoomOffY);
      }
    } else if (Game.gameState === STATE.Shop) {
      this.shop.render(g);
    } else if (Game.gameState === STATE.MusicPlayer) {
      this.musicPlayer.render(g);
    } else if (Game.gameState === STATE.Menu || Game.gameState === STATE.End
        || Game.gameState === STATE.Select || Game.gameState === STATE.Credits
        || Game.gameState === STATE.Settings
        || Game.gameState === STATE.Statistics
        || Game.gameState === STATE.AchievementsPage
        || Game.gameState === STATE.Customize
        || Game.gameState === STATE.Loadout
        || Game.gameState === STATE.CoinShopPage
        || Game.gameState === STATE.DailyPage) {
      this.menu.render(g);
    } else {
      this.menu.render(g);
      TrailPool.render(g);
      this.handler.render(g);
    }

    // Undo transition zoom
    if (hasTransZoom) {
      const z = 1 + this.transitionZoom * 0.06;
      g.scale(1 / z, 1 / z);
      g.translate(WIDTH * (z - 1) / 2, HEIGHT * (z - 1) / 2);
    }

    // Screen transition overlay — fast fade from black
    if (this.transitionAlpha > 0) {
      g.setColor(new Color(10, 12, 18, Math.trunc(this.transitionAlpha * 255)));
      g.fillRect(0, 0, WIDTH, HEIGHT);
    }

    // Slow-motion visual effect — chrome/white time warp
    if (Game.timeScale < 0.9 && (Game.gameState === STATE.Game || Game.gameState === STATE.Paused)) {
      const intensity = 1 - Game.timeScale; // ~0.7 at 0.3x speed

      // Chrome desaturation wash — white overlay
      const whiteAlpha = Math.trunc(intensity * 90);
      g.setColor(new Color(230, 235, 250, whiteAlpha));
      g.fillRect(0, 0, WIDTH, HEIGHT);

      // White vignette edges for time-warp feel
      const edgeAlpha = Math.trunc(intensity * 50);
      const edgeSize = 80;
      const edgeCol = new Color(255, 255, 255, edgeAlpha);
      g.setPaint(new GradientPaint(0, 0, edgeCol, edgeSize, 0, Game.SLOWMO_WHITE_CLEAR));
      g.fillRect(0, 0, edgeSize, HEIGHT);
      g.setPaint(new GradientPaint(WIDTH - edgeSize, 0, Game.SLOWMO_WHITE_CLEAR, WIDTH, 0, edgeCol));
      g.fillRect(WIDTH - edgeSize, 0, edgeSize, HEIGHT);
      g.setPaint(new GradientPaint(0, 0, edgeCol, 0, edgeSize, Game.SLOWMO_WHITE_CLEAR));
      g.fillRect(0, 0, WIDTH, edgeSize);
      g.setPaint(new GradientPaint(0, HEIGHT - edgeSize, Game.SLOWMO_WHITE_CLEAR, 0, HEIGHT, edgeCol));
      g.fillRect(0, HEIGHT - edgeSize, WIDTH, edgeSize);
      g.setColor(Color.WHITE); // reset paint to solid color mode
    }

    // Touch controls — drawn last and in screen space, so screen shake and the camera's
    // zoom/look-ahead don't wobble the thumb targets out from under the player's fingers.
    if (this.isTouch) {
      this.touch.render(g);
    }

    // ?touchdebug=1 — drawn in EVERY state and regardless of isTouch, so it can still report
    // "touch was never detected" or "the browser never sent a pointermove".
    if (TouchControls.debugEnabled) {
      this.touch.renderDebugPanel(g);
    }

    // FPS counter — in-game only, bottom-left (togglable in settings)
    if (Settings.getShowFps() && (Game.gameState === STATE.Game || Game.gameState === STATE.Paused)) {
      g.setFont(PageRenderer.SMALL_FONT);
      g.setColor(PageRenderer.TEXT_MUTED);
      g.drawString('FPS: ' + this.fps, 24, HEIGHT - 12);
    }

    g.dispose();
  }

  private renderBossIntro(g: Graphics2D): void {
    const WIDTH = Game.WIDTH;
    const HEIGHT = Game.HEIGHT;

    // Dim overlay — builds up during warning
    const dimProgress = Math.min(Game.bossIntroTimer / 80, 1);
    if (Game.bossIntroTimer < 100) {
      g.setColor(new Color(10, 5, 5, Math.trunc(dimProgress * 120)));
      g.fillRect(0, 0, WIDTH, HEIGHT);
    } else {
      // Flash on crash then fade
      const crashFade = Math.max(0, 1 - (Game.bossIntroTimer - 100) / 30);
      g.setColor(new Color(10, 5, 5, Math.trunc(crashFade * 80)));
      g.fillRect(0, 0, WIDTH, HEIGHT);
    }

    // Enemy explosion particles
    if (Game.enemyExplosions !== null) {
      for (const p of Game.enemyExplosions) {
        if (p[8] <= 0) continue;
        const alpha = Math.trunc(p[8] * 255);
        g.setColor(new Color(Math.trunc(p[5]), Math.trunc(p[6]), Math.trunc(p[7]), Math.min(alpha, 255)));
        const s = Math.trunc(p[4]);
        g.fillRoundRect(Math.trunc(p[0]) - Math.trunc(s / 2), Math.trunc(p[1]) - Math.trunc(s / 2), s, s, 3, 3);
      }
    }

    // WARNING text — pulsing red
    if (Game.bossIntroTimer < 100) {
      const pulse = Math.sin(Game.bossIntroTimer * 0.2) * 0.4 + 0.6;
      const textAlpha = Math.trunc(pulse * 255 * dimProgress);

      g.setFont(Game.BOSS_WARN_FONT);
      g.setColor(new Color(235, 60, 60, Math.min(textAlpha, 255)));
      let fm = g.getFontMetrics();
      const warn = 'WARNING';
      g.drawString(warn, Math.trunc((WIDTH - fm.stringWidth(warn)) / 2), Math.trunc(HEIGHT / 2) - 10);

      // Subtitle
      g.setFont(Game.BOSS_SUB_FONT);
      g.setColor(new Color(235, 60, 60, Math.min(Math.trunc(textAlpha * 0.6), 255)));
      fm = g.getFontMetrics();
      const sub = 'BOSS INCOMING';
      g.drawString(sub, Math.trunc((WIDTH - fm.stringWidth(sub)) / 2), Math.trunc(HEIGHT / 2) + 30);

      // Red border lines pulse
      const lineAlpha = Math.trunc(pulse * 100 * dimProgress);
      g.setColor(new Color(235, 60, 60, Math.min(lineAlpha, 255)));
      g.fillRect(0, 0, WIDTH, 3);
      g.fillRect(0, HEIGHT - 3, WIDTH, 3);
      g.fillRect(0, 0, 3, HEIGHT);
      g.fillRect(WIDTH - 3, 0, 3, HEIGHT);
    }

    // White flash on boss crash
    if (Game.bossIntroTimer >= 98 && Game.bossIntroTimer <= 110) {
      const flashT = 1 - (Game.bossIntroTimer - 98) / 12;
      g.setColor(new Color(255, 255, 255, Math.trunc(flashT * 180)));
      g.fillRect(0, 0, WIDTH, HEIGHT);
    }
  }

  private updateSpeedLines(): void {
    const WIDTH = Game.WIDTH;
    const HEIGHT = Game.HEIGHT;
    this.speedLineTimer++;

    // Decay existing lines
    for (const line of this.speedLines) {
      if (line[4] > 0) line[4] -= 0.04;
    }

    // Spawn new lines proportional to speed
    const threshold = 2;
    if (this.playerSpeed > threshold && this.speedLineTimer % 2 === 0) {
      const intensity = Math.min((this.playerSpeed - threshold) / 10, 1);
      // Only spawn with probability based on intensity
      if (this.r.nextFloat() < intensity) {
        // Find a dead slot
        for (const line of this.speedLines) {
          if (line[4] <= 0) {
            // Spawn from the edge opposite to movement direction
            const moveAngle = Math.atan2(this.playerVelY, this.playerVelX);
            // Lines come from the edges ahead of movement
            const spread = this.r.nextFloat() * Math.PI - Math.PI / 2;
            const lineAngle = moveAngle + Math.PI + spread;

            // Start position — at screen edge in the movement direction
            const edgeX = WIDTH / 2 + Math.cos(moveAngle) * (WIDTH / 2 + 20);
            const edgeY = HEIGHT / 2 + Math.sin(moveAngle) * (HEIGHT / 2 + 20);
            // Randomize along the perpendicular
            const perpX = -Math.sin(moveAngle) * (this.r.nextFloat() - 0.5) * WIDTH * 0.6;
            const perpY = Math.cos(moveAngle) * (this.r.nextFloat() - 0.5) * HEIGHT * 0.6;

            line[0] = edgeX + perpX;
            line[1] = edgeY + perpY;
            line[2] = lineAngle;
            line[3] = 40 + this.r.nextFloat() * 60 * intensity; // length
            line[4] = 0.6 + this.r.nextFloat() * 0.4; // life
            break;
          }
        }
      }
    }
  }

  private renderSpeedLines(g: Graphics2D): void {
    for (const line of this.speedLines) {
      if (line[4] <= 0) continue;
      const alpha = Math.trunc(line[4] * 40);
      g.setColor(new Color(230, 234, 240, Math.min(alpha, 255)));
      const x1 = line[0];
      const y1 = line[1];
      const x2 = x1 + Math.cos(line[2]) * line[3];
      const y2 = y1 + Math.sin(line[2]) * line[3];
      g.drawLine(Math.trunc(x1), Math.trunc(y1), Math.trunc(x2), Math.trunc(y2));
    }
  }

  private renderBeatVisuals(g: Graphics2D, beat: number): void {
    const WIDTH = Game.WIDTH;
    const HEIGHT = Game.HEIGHT;
    const density = GamePalette.getParticleDensity() * Settings.getParticleDensityMultiplier();
    const distortion = GamePalette.getDistortion();

    // Background color tint — stronger on harder difficulties
    const tintAlpha = Math.trunc(12 * density);
    g.setColor(GamePalette.bgTint(tintAlpha));
    g.fillRect(0, 0, WIDTH, HEIGHT);

    // Insane screen distortion — subtle chromatic-like edge tint
    if (distortion > 0) {
      const pulse = Math.sin(this.geoPhase * 3) * 0.5 + 0.5;
      const dAlpha = Math.trunc(pulse * 12 * distortion);
      g.setColor(new Color(255, 0, 0, dAlpha));
      g.fillRect(0, 0, 3, HEIGHT);
      g.setColor(new Color(0, 0, 255, dAlpha));
      g.fillRect(WIDTH - 3, 0, 3, HEIGHT);
    }

    // Grid dots — expand on beat, denser spacing on harder difficulties
    if (Settings.getGridDots()) {
      const gridSpacing = density > 1.5 ? 30 : density > 1.1 ? 35 : 40;
      const baseDotSize = 2;
      const beatDotSize = baseDotSize + Math.trunc(beat * 3 * density);
      const baseAlpha = Math.trunc((22 + beat * 35) * Math.min(density, 1.4));
      g.setColor(GamePalette.accent(Math.min(baseAlpha, 255)));
      for (let x = 20; x < WIDTH; x += gridSpacing) {
        for (let y = 20; y < HEIGHT; y += gridSpacing) {
          g.fillRect(x - Math.trunc(beatDotSize / 2), y - Math.trunc(beatDotSize / 2), beatDotSize, beatDotSize);
        }
      }
    }

    // Edge pulse — vignette throbs on beat, stronger on harder difficulties
    if (beat > 0.1) {
      const vigAlpha = Math.trunc(beat * 25 * density);
      const vigW = Math.trunc(4 * density);
      g.setColor(GamePalette.accent(Math.min(vigAlpha, 255)));
      g.fillRect(0, 0, WIDTH, vigW);
      g.fillRect(0, HEIGHT - vigW, WIDTH, vigW);
      g.fillRect(0, 0, vigW, HEIGHT);
      g.fillRect(WIDTH - vigW, 0, vigW, HEIGHT);
    }
  }

  private renderGeoLayers(g: Graphics2D): void {
    const WIDTH = Game.WIDTH;
    const HEIGHT = Game.HEIGHT;

    // Redraw cache every 4 frames (15fps is plenty for slow-moving bg elements)
    if (this.geoCache === null || this.geoCache.getWidth() !== WIDTH || ++this.geoCacheFrame >= 4) {
      this.geoCacheFrame = 0;
      if (this.geoCache === null || this.geoCache.getWidth() !== WIDTH) {
        this.geoCache = g.getDeviceConfiguration().createCompatibleImage(WIDTH, HEIGHT, Transparency.TRANSLUCENT);
      }
      const g2 = this.geoCache.createGraphics();
      g2.resetFrame();
      g2.setComposite(AlphaComposite.Clear);
      g2.fillRect(0, 0, WIDTH, HEIGHT);
      g2.setComposite(AlphaComposite.SrcOver);

      const cx = Math.trunc(WIDTH / 2);
      const cy = Math.trunc(HEIGHT / 2);
      const t = this.geoPhase;

      // Layer 1 — Large slow-rotating triangles
      g2.setStroke(new BasicStroke(1.5));
      g2.setColor(GamePalette.accent(10));
      for (let i = 0; i < 3; i++) {
        const angle = t * 0.4 + i * (Math.PI * 2 / 3);
        const ox = Math.cos(t * 0.15 + i) * 80;
        const oy = Math.sin(t * 0.12 + i * 1.5) * 60;
        this.drawRotatedTriangle(g2, cx + Math.trunc(ox), cy + Math.trunc(oy), 200 + i * 40, angle);
      }

      // Layer 2 — Hexagon ring
      g2.setColor(GamePalette.accent(8));
      g2.setStroke(new BasicStroke(1));
      const hexAngle = t * -0.25;
      for (let i = 0; i < 6; i++) {
        const a = hexAngle + i * (Math.PI / 3);
        this.drawHexagon(g2, cx + Math.trunc(Math.cos(a) * 280), cy + Math.trunc(Math.sin(a) * 280),
          50 + Math.trunc(Math.sin(t + i) * 10), a * 0.5);
      }
      g2.setColor(GamePalette.accent(12));
      this.drawHexagon(g2, cx, cy, 160, t * 0.15);

      // Layer 3 — Diagonal grid lines (tinted by difficulty)
      const dens = GamePalette.getParticleDensity() * Settings.getParticleDensityMultiplier();
      const lineSpacing = dens > 1.5 ? 55 : dens > 1.1 ? 65 : 80;
      g2.setStroke(new BasicStroke(1));
      g2.setColor(GamePalette.accent(Math.trunc(10 * dens)));
      const go1 = (t * 30) % lineSpacing;
      for (let i = -HEIGHT; i < WIDTH + HEIGHT; i += lineSpacing) {
        g2.drawLine(Math.trunc(i + go1), 0, Math.trunc(i + go1 - HEIGHT * 0.6), HEIGHT);
      }
      g2.setColor(GamePalette.accent(Math.trunc(6 * dens)));
      const go2 = (t * 18) % lineSpacing;
      for (let i = -HEIGHT; i < WIDTH + HEIGHT; i += lineSpacing) {
        g2.drawLine(Math.trunc(i + go2), 0, Math.trunc(i + go2 + HEIGHT * 0.6), HEIGHT);
      }

      g2.dispose();
    }
    g.drawImage(this.geoCache, 0, 0, null);
  }

  private drawRotatedTriangle(g: Graphics2D, cx: number, cy: number, size: number, angle: number): void {
    const xp = new Array<number>(3);
    const yp = new Array<number>(3);
    for (let i = 0; i < 3; i++) {
      const a = angle + i * (Math.PI * 2 / 3);
      xp[i] = cx + Math.trunc(Math.cos(a) * size / 2);
      yp[i] = cy + Math.trunc(Math.sin(a) * size / 2);
    }
    g.drawPolygon(xp, yp, 3);
  }

  private drawHexagon(g: Graphics2D, cx: number, cy: number, size: number, angle: number): void {
    const xp = new Array<number>(6);
    const yp = new Array<number>(6);
    for (let i = 0; i < 6; i++) {
      const a = angle + i * (Math.PI / 3);
      xp[i] = cx + Math.trunc(Math.cos(a) * size);
      yp[i] = cy + Math.trunc(Math.sin(a) * size);
    }
    g.drawPolygon(xp, yp, 6);
  }

  private renderNeonWalls(g: Graphics2D, beat: number): void {
    const WIDTH = Game.WIDTH;
    const HEIGHT = Game.HEIGHT;

    const basePulse = Math.sin(Game.wallPulsePhase) * 0.3 + 0.7;
    let baseAlpha = Math.trunc((basePulse + beat * 0.6) * 40);
    baseAlpha = Math.min(baseAlpha, 80);

    // Thin border lines
    g.setColor(GamePalette.accent(baseAlpha));
    g.fillRect(0, 0, WIDTH, 2);
    g.fillRect(0, HEIGHT - 2, WIDTH, 2);
    g.fillRect(0, 0, 2, HEIGHT);
    g.fillRect(WIDTH - 2, 0, 2, HEIGHT);

    // Subtle edge glow (single layer, no GradientPaint)
    g.setColor(GamePalette.accent(Math.trunc(baseAlpha / 4)));
    g.fillRect(0, 0, WIDTH, 8);
    g.fillRect(0, HEIGHT - 8, WIDTH, 8);
    g.fillRect(0, 0, 8, HEIGHT);
    g.fillRect(WIDTH - 8, 0, 8, HEIGHT);

    // Impact flares — simple bright spots
    for (let i = 0; i < Game.wallFlareCount; i++) {
      const fx = Game.wallFlares[i][0];
      const fy = Game.wallFlares[i][1];
      const intensity = Game.wallFlares[i][2];
      const side = Math.trunc(Game.wallFlares[i][3]);
      const a = Math.min(255, Math.trunc(intensity * 160));
      const aw = Math.min(255, Math.trunc(intensity * 50));

      g.setColor(GamePalette.accent(a));
      if (side === 0) g.fillRect(Math.trunc(fx) - 40, 0, 80, 4);
      else if (side === 1) g.fillRect(Math.trunc(fx) - 40, HEIGHT - 4, 80, 4);
      else if (side === 2) g.fillRect(0, Math.trunc(fy) - 40, 4, 80);
      else g.fillRect(WIDTH - 4, Math.trunc(fy) - 40, 4, 80);

      // Wider faint glow
      g.setColor(GamePalette.accent(aw));
      if (side === 0) g.fillRect(Math.trunc(fx) - 60, 0, 120, 10);
      else if (side === 1) g.fillRect(Math.trunc(fx) - 60, HEIGHT - 10, 120, 10);
      else if (side === 2) g.fillRect(0, Math.trunc(fy) - 60, 10, 120);
      else g.fillRect(WIDTH - 10, Math.trunc(fy) - 60, 10, 120);
    }

    // Bounce particles
    for (let i = 0; i < Game.bounceParticleCount; i++) {
      const p = Game.bounceParticles[i];
      const pa = Math.min(255, Math.trunc(p[4] * 255));
      const size = 2 + Math.trunc(p[4] * 2);
      g.setColor(new Color(Math.trunc(p[5]), Math.trunc(p[6]), Math.trunc(p[7]), pa));
      g.fillOval(Math.trunc(p[0]) - Math.trunc(size / 2), Math.trunc(p[1]) - Math.trunc(size / 2), size, size);
    }
  }

  static clamp(v: number, min: number, max: number): number {
    if (v >= max) return max;
    if (v <= min) return min;
    return v;
  }

  /** Hide the OS cursor during gameplay; the game draws its own aiming cues. */
  setCursorHidden(hidden: boolean): void {
    this.canvas.classList.toggle('nocursor', hidden);
  }
}
